import { AfterViewInit, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { AvailableLanguages } from '@constants';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-cukr-step3',
  templateUrl: './cukr-step3.component.html',
  styleUrls: ['./cukr-step3.component.scss'],
  standalone: false,
})
export class CukrStep3Component implements OnInit, AfterViewInit, OnDestroy {
  private readonly translateService = inject(TranslateService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Input() mode: 'foreigner' | 'employee';
  @Input() formGroup: FormGroup;
  @Output() firstInputShiftTabPressed: EventEmitter<void> = new EventEmitter<void>();
  @Output() lastFieldTabPressed: EventEmitter<void> = new EventEmitter<void>();

  public controls: { [key: string]: FormControl } = {};
  public currentLang: AvailableLanguages;

  public ngOnInit(): void {
    this.currentLang = this.translateService.currentLang as AvailableLanguages;

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
    });

    Object.keys(this.formGroup.controls).forEach(fieldName => {
      this.controls[fieldName] = this.formGroup.controls[fieldName] as FormControl;
    });

    const relatedFields = [
      ['wasSentencedInPoland', 'sentenceReason'],
      ['currentlySubjectOfProceedings', 'proceedingsReason'],
      ['liabilitiesFromVerdicts', 'liabilitiesDescription'],
    ];

    relatedFields.forEach(([controlee, controlled]) => {
      if (this.controls[controlee].value === false) {
        this.controls[controlled].disable();
      }

      this.controls[controlee].valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
        if (!val) {
          this.controls[controlled].disable();
        } else {
          this.controls[controlled].enable();
        }
      });
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.hasAnyFieldAnyValue(this.formGroup.controls)) {
        this.formGroup.markAllAsTouched();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public firstInputKeydown($event: KeyboardEvent): void {
    if (!$event.shiftKey) {
      return;
    }

    if ($event.key?.toLowerCase() !== 'tab') {
      return;
    }

    this.firstInputShiftTabPressed.emit();
  }

  public emitLastFieldTabPressed($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();

    this.lastFieldTabPressed.emit();
  }

  private hasAnyFieldAnyValue(controls: { [key: string]: AbstractControl }): boolean {
    return Object.keys(controls).some(controlName => {
      const val = controls[controlName].value;

      if (Array.isArray(val) && val.length > 0) {
        return true;
      }

      if (val !== '' && val !== undefined && val !== null) {
        return true;
      }

      return false;
    });
  }
}
